import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReplayIcon from "@mui/icons-material/Replay";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useDealTasks } from "@/hooks/useDealTasks";
import { useGlobalProvider } from "@/context/GlobalContext";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { useReferenceLists } from "@/context/GlobalGetLists";
import { useNotifications } from "@/context/NotificationsContext";
import {
  createDealTask,
  updateDealTask,
} from "@/services/dealTasksApi";
import type { DealTask } from "@/types/dealTask";
import { formatTaskTimestamps } from "@/utils/formatDate";
import { getInitials, getColorForName } from "@/utils/noteUtils";
import { CustomSingleSelect } from "@/components/common/inputFields/customSingleSelect";
import { CustomButton } from "@/components/common/customButton";
import { CustomIconButton } from "@/components/common/customIconButton";
import { ConfirmDialog } from "@/components/common/confirmDialog";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import { TaskStatusBadge } from "@/components/tasks/TaskStatusBadge";
import pencilIcon from "@/assets/svg/edit.svg";
import binIcon from "@/assets/svg/bin.svg";
import "@/assets/css/summary.css";

const AVATAR_SIZE = 22;

const brandIconFilter =
  "invert(21%) sepia(48%) saturate(1166%) hue-rotate(163deg) brightness(92%) contrast(89%)";

const compactIconSx = { p: 0.15 };

const COMPLETED_TASK_CARD_BORDER = "#12B76A";
const COMPLETED_TASK_CARD_BG = "rgba(18, 183, 106, 0.08)";
const TASK_CARD_BG = "#fff";
const TASK_CARD_BORDER = "rgba(0, 0, 0, 0.12)";
const TASK_DATE_COLOR = "#b8c0c8";

function taskCardPaperSx(isCompleted: boolean, isExpanded: boolean) {
  const border = isCompleted ? COMPLETED_TASK_CARD_BORDER : TASK_CARD_BORDER;
  const bg = isCompleted ? COMPLETED_TASK_CARD_BG : TASK_CARD_BG;

  return {
    p: 1.25,
    cursor: "pointer",
    transition: "box-shadow 0.15s ease",
    backgroundColor: `${bg} !important`,
    borderColor: `${border} !important`,
    ...(isExpanded && {
      borderColor: "primary.main !important",
      boxShadow: "0px 1px 5px 0px rgba(44, 62, 80, 0.12) !important",
    }),
    "&:hover": {
      backgroundColor: `${bg} !important`,
      boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08) !important",
    },
  };
}

function InitialsAvatar({ name, title }: { name: string | null; title?: string }) {
  const displayName = name ?? "?";
  const initials = getInitials(displayName);
  const bgColor = getColorForName(displayName);

  return (
    <Tooltip title={title ?? displayName} arrow>
      <Box
        sx={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          borderRadius: "50%",
          backgroundColor: bgColor,
          color: "#FFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: "0.625rem",
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {initials}
      </Box>
    </Tooltip>
  );
}

interface TasksEditorProps {
  dealId: number;
  highlightTaskId?: number | null;
}

const TasksEditor = ({
  dealId,
  highlightTaskId,
}: TasksEditorProps) => {
  const { tasks, loading, error, refetch } = useDealTasks(dealId);
  const { user, userLoading } = useGlobalProvider();
  const { canEdit } = useCurrentUser();
  const { users } = useReferenceLists();
  const { refetchUnreadCount, markReadByEntity } = useNotifications();

  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState<number | "">("");
  const [creating, setCreating] = useState(false);
  const [editTaskId, setEditTaskId] = useState<number | null>(null);
  const [draftDescription, setDraftDescription] = useState("");
  const [draftAssigneeId, setDraftAssigneeId] = useState<number | "">("");
  const [reassignTaskId, setReassignTaskId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<DealTask | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null);
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  // Highlight each requested task once; later refetches must not re-expand/re-scroll/re-flash it.
  const handledHighlightIdRef = useRef<number | null>(null);
  const highlightTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const highlightedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // When the requested task changes (or on unmount), cancel any pending scroll/flash.
    return () => {
      highlightTimersRef.current.forEach(clearTimeout);
      highlightTimersRef.current = [];
      if (highlightedElementRef.current) {
        highlightedElementRef.current.style.backgroundColor = "";
        highlightedElementRef.current = null;
      }
      handledHighlightIdRef.current = null;
    };
  }, [highlightTaskId]);

  useEffect(() => {
    if (!highlightTaskId || loading || handledHighlightIdRef.current === highlightTaskId) return;
    const elementId = `deal-task-${highlightTaskId}`;
    if (!document.getElementById(elementId)) return;
    handledHighlightIdRef.current = highlightTaskId;
    setExpandedTaskId(highlightTaskId);
    const flashTimer = setTimeout(() => {
      // Re-query so we never mutate a node that was replaced by a re-render.
      const element = document.getElementById(elementId);
      if (!element) return;
      highlightedElementRef.current = element;
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.style.backgroundColor = "#fff3cd";
      const clearTimer = setTimeout(() => {
        element.style.backgroundColor = "";
        highlightedElementRef.current = null;
      }, 3000);
      highlightTimersRef.current.push(clearTimer);
    }, 300);
    highlightTimersRef.current.push(flashTimer);
  }, [highlightTaskId, tasks, loading]);

  const handleOpenTask = async (task: DealTask) => {
    const willExpand = expandedTaskId !== task.id;
    setExpandedTaskId((prev) => (prev === task.id ? null : task.id));
    if (!willExpand || !user?.tenfiftyid) return;
    await markReadByEntity({ entity_type: "deal_task", entity_id: task.id });
  };

  const handleCreate = async () => {
    if (!description.trim() || !assigneeId || userLoading || !user?.tenfiftyid) return;
    setCreating(true);
    try {
      const response = await createDealTask(dealId, {
        description: description.trim(),
        assigned_to_id: Number(assigneeId),
        created_by_id: user.tenfiftyid,
      });
      if (response?.success) {
        setDescription("");
        setAssigneeId("");
        await refetch();
        await refetchUnreadCount();
      } else {
        showErrorFromResponse(response);
      }
    } catch (err) {
      console.error("Error creating task:", err);
      showError(undefined, "Failed to create task. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (task: DealTask, status: "open" | "completed") => {
    if (userLoading || !user?.tenfiftyid) return;
    try {
      const response = await updateDealTask(task.id, {
        status,
        updated_by_id: user.tenfiftyid,
      });
      if (response?.success) {
        await refetch();
      } else {
        showErrorFromResponse(response);
      }
    } catch (err) {
      console.error("Error updating task status:", err);
      showError(undefined, "Failed to update task status. Please try again.");
    }
  };

  const handleSaveEdit = async (task: DealTask) => {
    if (userLoading || !user?.tenfiftyid) return;
    if (!draftDescription.trim()) return;
    if (draftDescription === task.description) {
      setEditTaskId(null);
      return;
    }
    try {
      const response = await updateDealTask(task.id, {
        description: draftDescription.trim(),
        updated_by_id: user.tenfiftyid,
      });
      if (response?.success) {
        setEditTaskId(null);
        await refetch();
      } else {
        showErrorFromResponse(response);
      }
    } catch (err) {
      console.error("Error updating task:", err);
      showError(undefined, "Failed to update task. Please try again.");
    }
  };

  const handleSaveReassign = async (task: DealTask) => {
    if (userLoading || !user?.tenfiftyid || !draftAssigneeId) return;
    if (Number(draftAssigneeId) === task.assigned_to_id) {
      setReassignTaskId(null);
      return;
    }
    try {
      const response = await updateDealTask(task.id, {
        assigned_to_id: Number(draftAssigneeId),
        updated_by_id: user.tenfiftyid,
      });
      if (response?.success) {
        setReassignTaskId(null);
        await refetch();
        await refetchUnreadCount();
      } else {
        showErrorFromResponse(response);
      }
    } catch (err) {
      console.error("Error reassigning task:", err);
      showError(undefined, "Failed to reassign task. Please try again.");
    }
  };

  const startEdit = (task: DealTask) => {
    setEditTaskId(task.id);
    setDraftDescription(task.description);
    setReassignTaskId(null);
  };

  const startReassign = (task: DealTask) => {
    setReassignTaskId(task.id);
    setDraftAssigneeId(task.assigned_to_id ?? "");
    setEditTaskId(null);
  };

  if (loading && tasks.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: "#f2f5f9" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        className="summary-card"
        sx={{ p: 1.5, mb: 2.5, backgroundColor: `${TASK_CARD_BG} !important` }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            label="Task description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            disabled={!canEdit || creating || userLoading}
            sx={{ "& .MuiInputBase-root": { fontSize: "0.8125rem", backgroundColor: "#fff" } }}
          />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomSingleSelect
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value as number)}
                options={users}
                labelKey="name"
                placeholder="Assign to"
                fullWidth
                disabled={!canEdit || creating || userLoading}
              />
            </Box>
            <CustomButton
              text="Create"
              handleButtonPress={handleCreate}
              isButtonDisabled={
                !canEdit ||
                creating ||
                userLoading ||
                !user?.tenfiftyid ||
                !description.trim() ||
                !assigneeId
              }
            />
          </Box>
        </Box>
      </Paper>

      <ConfirmDialog
        open={dialogOpen}
        endpoint="deal-tasks"
        id={taskToDelete?.id || 0}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          void refetch();
          setTaskToDelete(null);
        }}
      />

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />

      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: "10px" }}>
        {tasks.length === 0 && !loading && (
          <Typography color="text.secondary" variant="body2">
            No tasks yet.
          </Typography>
        )}
        {tasks.map((task) => {
          const isEditing = editTaskId === task.id;
          const isReassigning = reassignTaskId === task.id;
          const isExpanded = expandedTaskId === task.id;

          const renderActions = () => {
            if (isEditing) {
              return (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CustomIconButton
                    tooltip="Save"
                    icon={<CheckIcon />}
                    onClick={() => void handleSaveEdit(task)}
                    disabled={
                      !canEdit ||
                      userLoading ||
                      !user?.tenfiftyid ||
                      !draftDescription.trim()
                    }
                    iconButtonSx={compactIconSx}
                  />
                  <CustomIconButton
                    tooltip="Cancel"
                    icon={<CloseIcon />}
                    onClick={() => setEditTaskId(null)}
                    iconButtonSx={compactIconSx}
                  />
                </Box>
              );
            }

            if (isReassigning) {
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flex: 1, minWidth: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CustomSingleSelect
                      value={draftAssigneeId}
                      onChange={(e) => setDraftAssigneeId(e.target.value as number)}
                      options={users}
                      labelKey="name"
                      placeholder="Assign to"
                      fullWidth
                      heightOverride="28px"
                    />
                  </Box>
                  <CustomIconButton
                    tooltip="Save"
                    icon={<CheckIcon />}
                    onClick={() => void handleSaveReassign(task)}
                    disabled={!canEdit || userLoading || !user?.tenfiftyid}
                    iconButtonSx={compactIconSx}
                  />
                  <CustomIconButton
                    tooltip="Cancel"
                    icon={<CloseIcon />}
                    onClick={() => setReassignTaskId(null)}
                    iconButtonSx={compactIconSx}
                  />
                </Box>
              );
            }

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {task.status === "open" ? (
                    <>
                      <CustomIconButton
                        tooltip="Reassign"
                        icon={<SwapHorizOutlinedIcon sx={{ fontSize: 18 }} />}
                        onClick={() => startReassign(task)}
                        disabled={!canEdit || userLoading}
                        iconButtonSx={compactIconSx}
                      />
                      <CustomIconButton
                        tooltip="Mark complete"
                        icon={<TaskAltIcon sx={{ fontSize: 18 }} />}
                        onClick={() => void handleStatusChange(task, "completed")}
                        disabled={!canEdit || userLoading || !user?.tenfiftyid}
                        iconButtonSx={compactIconSx}
                      />
                    </>
                  ) : (
                    <CustomIconButton
                      tooltip="Reopen"
                      icon={<ReplayIcon sx={{ fontSize: 18 }} />}
                      onClick={() => void handleStatusChange(task, "open")}
                      disabled={!canEdit || userLoading || !user?.tenfiftyid}
                      iconButtonSx={compactIconSx}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    pl: 0.25,
                  }}
                >
                  <CustomIconButton
                    tooltip="Edit"
                    icon={
                      <Box
                        component="img"
                        src={pencilIcon}
                        alt=""
                        sx={{
                          width: 18,
                          height: 18,
                          display: "block",
                          filter: brandIconFilter,
                        }}
                      />
                    }
                    onClick={() => startEdit(task)}
                    disabled={!canEdit || userLoading}
                    iconButtonSx={compactIconSx}
                  />
                  <CustomIconButton
                    tooltip="Delete"
                    icon={
                      <Box
                        component="img"
                        src={binIcon}
                        alt=""
                        sx={{
                          width: 18,
                          height: 18,
                          display: "block",
                          filter: brandIconFilter,
                        }}
                      />
                    }
                    onClick={() => {
                      setTaskToDelete(task);
                      setDialogOpen(true);
                    }}
                    disabled={!canEdit || userLoading}
                    iconButtonSx={compactIconSx}
                  />
                </Box>
              </Box>
            );
          };

          const isCompleted = task.status === "completed";

          return (
            <Paper
              key={task.id}
              id={`deal-task-${task.id}`}
              elevation={0}
              className="summary-card"
              onClick={() => void handleOpenTask(task)}
              sx={taskCardPaperSx(isCompleted, isExpanded)}
            >
              <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexShrink: 0,
                      pt: 0.125,
                    }}
                  >
                    <InitialsAvatar
                      name={task.created_by_name}
                      title={`Assigned by ${task.created_by_name ?? "Unknown"}`}
                    />
                    <ChevronRightIcon sx={{ fontSize: 14, color: "#adadad", flexShrink: 0 }} />
                    <InitialsAvatar
                      name={task.assigned_to_name}
                      title={`Assigned to ${task.assigned_to_name ?? "Unassigned"}`}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {isEditing ? (
                      <TextField
                        value={draftDescription}
                        onChange={(e) => setDraftDescription(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            fontSize: "0.8125rem",
                            py: 0.25,
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.8125rem",
                          fontWeight: isCompleted ? 400 : 500,
                          color: isCompleted ? "text.secondary" : "#182737",
                          lineHeight: 1.35,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {task.description}
                      </Typography>
                    )}

                    {isReassigning && (
                      <Box sx={{ mt: 0.5 }}>{renderActions()}</Box>
                    )}
                  </Box>

                  {!isReassigning && (
                    <Box sx={{ flexShrink: 0, alignSelf: "flex-start", pt: 0.125 }}>
                      {renderActions()}
                    </Box>
                  )}
                </Box>

                {!isReassigning && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.5,
                      minHeight: 22,
                    }}
                  >
                    <TaskStatusBadge status={task.status} />
                    <Typography
                      component="span"
                      noWrap={!isCompleted}
                      sx={{
                        color: `${TASK_DATE_COLOR} !important`,
                        fontSize: "0.625rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {formatTaskTimestamps(task)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default TasksEditor;
